import { useState, useMemo, useCallback, useEffect } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import useRelativitySyncProgress from '@/api/hooks/useRelativitySyncProgress';
import { APIBaseChronos } from '@/api/hosts';
import useStartRelativitySync from '@/api/mutations/useStartRelativitySync';
import useGetConfiguredRelativityWorkspaces from '@/api/queries/useGetConfiguredRelativityWorkspaces';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import useGetRelativitySyncPreview from '@/api/queries/useGetRelativitySyncPreview';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Label } from '@/new/components/shadcn/label';
import { Progress } from '@/new/components/shadcn/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

export function RelativitySync() {
  const { matterId } = useParams();
  const { accessToken } = useAuthInfo();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | undefined>();
  const [activeJobId, setActiveJobId] = useState<string | undefined>();
  const [includeExisting, setIncludeExisting] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  // Check Relativity authentication status
  const { isAuthenticated, isLoading: isLoadingAuth, isConnecting, connectRelativity } = useRelativityAuth();

  // Fetch matter data to check if it's linked to a Relativity workspace
  const { data: matter } = useGetMatterV2(matterId);

  // Fetch configured workspaces (only if authenticated)
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetConfiguredRelativityWorkspaces({
    enabled: isAuthenticated,
  });

  // Check if matter is linked to a specific workspace
  const linkedWorkspaceId = matter?.relativityWorkspaceId;
  const linkedWorkspace = useMemo(() => {
    if (!linkedWorkspaceId || !workspaces) return null;
    return workspaces.find((w) => w.workspaceId === linkedWorkspaceId);
  }, [linkedWorkspaceId, workspaces]);

  // Auto-select the linked workspace when available
  useEffect(() => {
    if (linkedWorkspaceId && !selectedWorkspaceId) {
      setSelectedWorkspaceId(linkedWorkspaceId);
    }
  }, [linkedWorkspaceId, selectedWorkspaceId]);

  // Fetch preview when workspace selected
  const {
    data: preview,
    isLoading: isLoadingPreview,
    refetch: refetchPreview,
  } = useGetRelativitySyncPreview(matterId, selectedWorkspaceId, {
    enabled: !!selectedWorkspaceId,
    includeExisting,
  });

  // Start sync mutation
  const { mutateAsync: startSync, isLoading: isStarting } = useStartRelativitySync();

  // SSE progress tracking
  const { progress, complete, error: progressError } = useRelativitySyncProgress(activeJobId);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (complete) return 100;
    if (!progress || progress.totalFiles === 0) return 0;
    return Math.round((progress.processedFiles / progress.totalFiles) * 100);
  }, [progress, complete]);

  const handleStartSync = async () => {
    if (!matterId || !selectedWorkspaceId) {
      toast.error('Please select a workspace');
      return;
    }

    try {
      const result = await startSync({ matterId, workspaceId: selectedWorkspaceId, includeExisting });
      setActiveJobId(result.jobId);
      toast.success(`Started syncing metadata for ${result.filesFound.toLocaleString()} files`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start sync');
    }
  };

  const handleReset = () => {
    setActiveJobId(undefined);
    refetchPreview();
  };

  const handleDownloadReport = useCallback(async () => {
    if (!activeJobId || !accessToken) return;

    setIsDownloadingReport(true);
    try {
      const response = await fetch(`${APIBaseChronos}/api/relativity/sync/${activeJobId}/report`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'relativity-sync-report.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download report');
    } finally {
      setIsDownloadingReport(false);
    }
  }, [activeJobId, accessToken]);

  if (!matterId) return null;

  // Determine subheading based on state
  const subheading = activeJobId ? (
    <p className="text-sm text-gray-500">{complete ? 'Sync complete' : 'Syncing metadata from Relativity...'}</p>
  ) : (
    <p className="text-sm text-gray-500">
      Sync metadata from Relativity to add metadata to files missing it, or update metadata based on the latest in
      Relativity.
    </p>
  );

  // Render content based on state
  const renderContent = () => {
    // Loading auth state
    if (isLoadingAuth) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
          Checking Relativity connection...
        </div>
      );
    }

    // Not authenticated - show connect prompt
    if (!isAuthenticated) {
      return (
        <div className="max-w-2xl">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-teal-100 p-3">
                <Icon className="lucide--link h-6 w-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900">Connect to Relativity</h3>
                <p className="mt-1 text-sm text-gray-600">
                  To sync metadata from Relativity, you need to connect your Relativity account first. This will open a
                  new window to authenticate with Relativity.
                </p>
                <PermissionGate
                  permission={Permission.RelativityImport}
                  uiMode="disabled"
                  disabledMessage="You do not have permission to connect to a Relativity account"
                >
                  <Button variant="primary" className="mt-4" onClick={connectRelativity} disabled={isConnecting}>
                    {isConnecting ? (
                      <>
                        <Icon className="lucide--loader-2 mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Icon className="lucide--external-link mr-2 h-4 w-4" />
                        Connect to Relativity
                      </>
                    )}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Active sync job - show progress/completion
    if (activeJobId) {
      return (
        <div className="max-w-2xl space-y-6">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {complete
                  ? 'Completed'
                  : progress?.status === 'preparing'
                    ? progress.preparingMessage || 'Preparing...'
                    : `Processing: ${(progress?.processedFiles ?? 0).toLocaleString()} / ${(progress?.totalFiles ?? 0).toLocaleString()} files`}
              </span>
              <span className="font-medium text-gray-900">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 px-5">
              <div className="text-2xl font-semibold text-green-700">
                {(complete?.matched ?? progress?.matched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-green-600">Matched</div>
            </div>
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 px-5">
              <div className="text-2xl font-semibold text-yellow-700">
                {(complete?.unmatched ?? progress?.unmatched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-yellow-600">Unmatched</div>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 px-5">
              <div className="text-2xl font-semibold text-red-700">
                {(complete?.failed ?? progress?.failed ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-red-600">Failed</div>
            </div>
          </div>

          {/* Completion details */}
          {complete && (
            <div className="space-y-4">
              {complete.unmatchedFiles && complete.unmatchedFiles.length > 0 && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                  <h4 className="mb-2 font-medium text-yellow-800">
                    Unmatched Files ({complete.unmatchedFiles.length})
                  </h4>
                  <p className="mb-2 text-sm text-yellow-700">
                    These files could not be matched to Relativity documents by Control Number:
                  </p>
                  <ul className="max-h-40 overflow-y-auto text-sm text-yellow-700">
                    {complete.unmatchedFiles.slice(0, 20).map((file) => (
                      <li key={file.fileId} className="truncate">
                        {file.fileName} ({file.controlNumber})
                      </li>
                    ))}
                    {complete.unmatchedFiles.length > 20 && (
                      <li className="italic">...and {complete.unmatchedFiles.length - 20} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button onClick={handleDownloadReport} disabled={isDownloadingReport}>
                  {isDownloadingReport ? (
                    <>
                      <Icon className="lucide--loader-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Icon className="lucide--download" />
                      Download Report
                    </>
                  )}
                </Button>
                <Button onClick={handleReset}>Done</Button>
              </div>
            </div>
          )}

          {/* Error state */}
          {progressError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{progressError}</p>
              <Button variant="outline" className="mt-2" onClick={handleReset}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Default setup view
    return (
      <div className="max-w-2xl space-y-6">
        {/* Workspace selector */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Relativity Workspace</Label>
          {isLoadingWorkspaces ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
              Loading workspaces...
            </div>
          ) : linkedWorkspaceId ? (
            // Matter is linked to a specific workspace - show fixed workspace info
            <div className="mt-2 flex items-center gap-2 text-sm">
              <Icon className="lucide--link h-4 w-4 text-gray-400" />
              <span className="text-gray-700">
                Syncing from{' '}
                <span className="font-semibold">
                  {linkedWorkspace?.workspaceName ?? `Workspace ${linkedWorkspaceId}`}
                </span>
              </span>
            </div>
          ) : workspaces && workspaces.length > 0 ? (
            <Select
              value={selectedWorkspaceId?.toString()}
              onValueChange={(value) => setSelectedWorkspaceId(parseInt(value, 10))}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem key={workspace.workspaceId} value={workspace.workspaceId.toString()}>
                    {workspace.workspaceName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-700">
                No Relativity workspaces with field mappings configured. Please configure field mappings first in the
                Relativity integration settings.
              </p>
            </div>
          )}
        </div>

        {/* Preview section */}
        {selectedWorkspaceId && (
          <div className="space-y-4">
            {isLoadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
                Checking files...
              </div>
            ) : preview ? (
              <>
                {/* Field mapping status */}
                <div className="flex items-center gap-2">
                  {preview.fieldMappingConfigured ? (
                    <>
                      <Icon className="lucide--check-circle h-4 w-4 text-teal-500" />
                      <span className="text-sm text-teal-500">Field mappings configured</span>
                    </>
                  ) : (
                    <>
                      <Icon className="lucide--alert-circle h-4 w-4 text-yellow-500" />
                      <span className="text-sm text-yellow-700">Field mappings not configured for this workspace</span>
                    </>
                  )}
                </div>

                {/* Files count */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 px-5">
                  <div className="text-2xl font-semibold text-gray-900">
                    {includeExisting
                      ? (preview.filesWithoutMetadata + preview.filesWithMetadata).toLocaleString()
                      : preview.filesWithoutMetadata.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    {includeExisting ? (
                      <>
                        files to sync ({preview.filesWithoutMetadata.toLocaleString()} new +{' '}
                        {preview.filesWithMetadata.toLocaleString()} to update)
                      </>
                    ) : (
                      'files without metadata'
                    )}
                  </div>
                </div>

                {/* Update existing checkbox */}
                <div className="my-8 flex items-start space-x-3">
                  <Checkbox
                    id="includeExisting"
                    checked={includeExisting}
                    onCheckedChange={(checked) => setIncludeExisting(checked === true)}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="includeExisting"
                      className="mb-1 cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Update existing metadata
                    </Label>
                    <p className="text-sm text-gray-500">
                      When enabled, files that already have metadata will be updated with the latest values from
                      Relativity.
                    </p>
                  </div>
                </div>

                {/* Sample files */}
                {preview.sampleFiles.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Sample Files</Label>
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                      <table className="w-full text-sm">
                        <thead className="border-b border-gray-200 bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Filename</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Expected Control Number</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.sampleFiles.map((file) => (
                            <tr key={file.id} className="border-b border-gray-100 last:border-0">
                              <td className="truncate px-3 py-2 text-gray-900">{file.name}</td>
                              <td className="px-3 py-2 font-mono text-gray-600">{file.controlNumber}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Start sync button */}
                {((includeExisting && preview.filesWithoutMetadata + preview.filesWithMetadata > 0) ||
                  (!includeExisting && preview.filesWithoutMetadata > 0)) &&
                  preview.fieldMappingConfigured && (
                    <div className="pt-2">
                      <Button variant="primary" onClick={handleStartSync} disabled={isStarting}>
                        {isStarting ? (
                          <>
                            <Icon className="lucide--loader-2 mr-2 h-4 w-4 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <Icon className="lucide--refresh-cw mr-2 h-4 w-4" />
                            Start Sync
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                {preview.filesWithoutMetadata === 0 && !includeExisting && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <div className="flex items-center gap-2">
                      <Icon className="lucide--check-circle h-5 w-5 text-green-500" />
                      <span className="text-sm text-green-700">
                        All files in this matter already have metadata. Enable "Update existing metadata" to refresh
                        them.
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SettingsHeader title="Relativity Metadata Sync" subheading={subheading} />
      {renderContent()}
    </>
  );
}
